import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors } from 'styles/settings';
import { SwitchWithText } from 'components/switch/SwitchWithText';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { createStyles } from 'utils/createStyle';

export interface tableDataType {
    description: {
        icon: JSX.Element;
        text: string;
    };
    payCoordinatorOn: {
        icon: JSX.Element;
        text: string;
    };
    payCoordinatorOff: {
        icon: JSX.Element;
        text: string;
    };
}

export interface OrderingSettingsTableProps {
    title: string;
    subTitle?: JSX.Element;
    variant: 'Ship to' | 'Pay';
    data: tableDataType[];
    isToggleOn: boolean;
    setIsToggleOn: (value: boolean) => void;
    isDisabled?: boolean;
    learnMoreVariant: 'pay-coordinator-faqs' | 'ship-to-coordinator-faqs';
}

export const styles = createStyles({
    mainContainer: 'max-w-full border border-grey-light rounded-lg mt-3 mb-3',
    onHeader: ['border-r border-grey-light pl-0 pr-2 py-2 pt-5 w-3/12'],
    offHeader: ['pl-4 pr-0 py-2 pt-5 w-3/12'],
    disabled: ['opacity-50 bg-grey-accent'],
    learnMoreLink:
        'text-center bg-brand-light rounded-tl-lg rounded-br-lg text-brand text-xs font-bold px-7 py-3 no-underline',
});

const OrderingSettingsTable = ({
    title,
    subTitle,
    variant,
    isToggleOn,
    setIsToggleOn,
    data,
    isDisabled,
    learnMoreVariant,
}: OrderingSettingsTableProps) => {
    return (
        <div className={styles.mainContainer}>
            <div
                className={`max-w-full border-b border-grey-light p-6 ${
                    isDisabled ? styles.disabled : ''
                }`}
            >
                <div className="max-w-full flex items-center justify-between">
                    <span className="text-brand-dark text-xl font-semibold">{title}</span>
                    <SwitchWithText
                        isDisabled={isDisabled}
                        checked={isToggleOn}
                        onChange={setIsToggleOn}
                    />
                </div>
                {subTitle ? (
                    <div className="max-w-full flex items-center justify-between">
                        <span className="text-grey text-sm font-light sm:w-10/12 w-full">
                            {subTitle}
                        </span>
                        <div />
                    </div>
                ) : null}
            </div>
            <div className={`max-w-full px-6 overflow-x-auto ${isDisabled ? styles.disabled : ''}`}>
                <table className="w-full table-auto text-sm font-normal ">
                    <tbody>
                        <tr className="bg-white">
                            <td />
                            <td className={styles.onHeader}>
                                {variant} Coordinator:{' '}
                                <span className="text-success font-bold">ON</span>
                            </td>
                            <td className={styles.offHeader}>
                                {variant} Coordinator:{' '}
                                <span className="text-grey font-bold">OFF</span>
                            </td>
                        </tr>
                        {data.map((item, index) => (
                            <tr
                                key={index}
                                className={
                                    index % 2 === 0
                                        ? `${isDisabled ? 'bg-grey-7' : 'bg-grey-accent'}`
                                        : 'bg-white'
                                }
                            >
                                <td className=" pl-4 pr-1 py-2 font-semibold flex items-center">
                                    <div className="flex items-center">{item.description.icon}</div>
                                    <span className="ml-2 w-10/12">{item.description.text}</span>
                                </td>
                                <td className="border-r border-grey-light pl-0 pr-2 py-2 ">
                                    {item.payCoordinatorOn.icon}
                                    {item.payCoordinatorOn.text}
                                </td>
                                <td className="pl-4 pr-2 py-2 ">
                                    {item.payCoordinatorOff.icon}
                                    {item.payCoordinatorOff.text}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={`w-full h-8 ${isDisabled ? styles.disabled : ''}`} />
            <div className="max-w-full flex items-end justify-end pt-0">
                <div className={`flex-1 h-10 ${isDisabled ? styles.disabled : ''}`} />
                <a
                    className={styles.learnMoreLink}
                    target="_blank"
                    href={`https://www.raiseright.com/m/organization-settings-faqs#${learnMoreVariant}`}
                >
                    Learn More{' '}
                    <FontAwesomeIcon
                        icon={faExternalLink}
                        color={colors.brand}
                        className="ml-1.5"
                        size="1x"
                    />
                </a>
            </div>
        </div>
    );
};

export default OrderingSettingsTable;
